import { EventEmitter } from '@angular/core'
import { CitationMinuteNodes } from '../../../../../backend/src/citations/minutes-citations-api.dto'

type EventEmitters = {
  openDocument: EventEmitter<{ resourceId: number; resourcePageNumber: number }>
  openMeeting: EventEmitter<void>
}

export function createCitationGroupSpanElement(): HTMLSpanElement {
  const span = document.createElement('span')

  span.className = 'citation-group mceNonEditable'

  return span
}

export function createCallSentenceCitationSpan({
  editor,
  chunkRefIds,
  eventEmitters,
  startSentenceId,
  endSentenceId,
  label,
}: {
  editor: any
  chunkRefIds: string[]
  eventEmitters: EventEmitters
  startSentenceId: string
  endSentenceId: string
  label: number
}): HTMLSpanElement {
  const element = document.createElement('span')

  element.className = 'citation-group-item citation-call-sentence mceNonEditable'

  element.setAttribute('data-start-sentence-id', startSentenceId)
  element.setAttribute('data-end-sentence-id', endSentenceId)

  element.textContent = label.toString()

  const chunkRefElements = chunkRefIds.map((id) => editor.dom.select(`[data-chunk-id="${id}"]`)[0]).filter(Boolean)

  element.addEventListener('mouseenter', () => {
    chunkRefElements.forEach((chunkElement) => chunkElement.classList.add('citation-highlight'))
  })

  element.addEventListener('mouseleave', () => {
    chunkRefElements.forEach((chunkElement) => chunkElement.classList.remove('citation-highlight'))
  })

  element.addEventListener('click', () => {
    // Sets the cursor to the beginning of the referenced chunk.
    editor.selection.setCursorLocation(editor.dom.select(`[data-chunk-id="${chunkRefIds[0]}"]`)[0], 0)

    eventEmitters.openMeeting.emit()
  })

  return element
}

export function createCallResourceCitationSpan({
  editor,
  chunkRefQuery,
  chunkRefElement,
  eventEmitters,
  resourceId,
  resourcePageNumber,
  label,
}: {
  editor: any
  chunkRefQuery: string
  chunkRefElement: HTMLElement
  eventEmitters: EventEmitters
  resourceId: number
  resourcePageNumber: number
  label: number
}): HTMLSpanElement {
  const element = document.createElement('span')

  element.className = 'citation-group-item citation-call-resource mceNonEditable'

  element.setAttribute('data-call-resource-id', resourceId.toString())
  element.setAttribute('data-call-resource-page-number', resourcePageNumber.toString())

  element.textContent = label.toString()

  element.addEventListener('mouseenter', () => {
    chunkRefElement.classList.add('citation-highlight')
  })

  element.addEventListener('mouseleave', () => {
    chunkRefElement.classList.remove('citation-highlight')
  })

  element.addEventListener('click', () => {
    // Sets the cursor to the beginning of the referenced chunk.
    editor.selection.setCursorLocation(editor.dom.select(chunkRefQuery)[0], 0)

    eventEmitters.openDocument.emit({
      resourceId,
      resourcePageNumber,
    })
  })

  return element
}

export function containsCitations(editor: any): boolean {
  return editor.dom.select('span.citation-group').length > 0
}

export function insertCitationTags({ editor, citations, eventEmitters }: { editor: any; citations: CitationMinuteNodes; eventEmitters: EventEmitters }) {
  if (containsCitations(editor)) {
    return
  }

  for (const [parentNodeId, citationMinuteNode] of Object.entries(citations)) {
    // We use this to keep track of what label number to assign to each citation item within the same minute node.
    // We reset this every minute node as per product requirements.
    let labelIndex = 1

    for (const [nodeId, { chunks }] of Object.entries(citationMinuteNode.items)) {
      const parentElementQuery = `[data-parent-node-id="${parentNodeId}"][data-node-id="${nodeId}"]`
      const parentElement: HTMLElement | null = editor.dom.select(parentElementQuery)[0] ?? null

      if (parentElement === null) {
        console.error(`Could not find parent element with query selector: '${parentElementQuery}'.`)

        continue
      }

      let citationGroupElement = parentElement.querySelector('.citation-group')

      if (citationGroupElement === null) {
        citationGroupElement = createCitationGroupSpanElement()

        parentElement.appendChild(citationGroupElement)
      }

      for (const [chunkId, { callResourceCitations, callSentenceCitations }] of Object.entries(chunks)) {
        const chunkElementQuery = `[data-chunk-id="${chunkId}"]`
        const chunkElement: HTMLElement | null = parentElement.querySelector(chunkElementQuery) ?? null

        if (chunkElement === null) {
          console.error(`Could not find chunk element with query selector: '${chunkElementQuery}'.`)

          continue
        }

        for (const callSentenceCitation of callSentenceCitations) {
          const citationElement = createCallSentenceCitationSpan({
            editor,
            chunkRefIds: [chunkId, ...callSentenceCitation.duplicateChunkIds],
            eventEmitters,
            startSentenceId: callSentenceCitation.startSentenceId,
            endSentenceId: callSentenceCitation.endSentenceId,
            label: labelIndex,
          })

          citationGroupElement.appendChild(citationElement)

          labelIndex++
        }

        for (const callResourceCitation of callResourceCitations) {
          const citationElement = createCallResourceCitationSpan({
            editor,
            chunkRefQuery: chunkElementQuery,
            chunkRefElement: chunkElement,
            eventEmitters,
            resourceId: callResourceCitation.resourceId,
            resourcePageNumber: callResourceCitation.resourcePageNumber,
            label: labelIndex,
          })

          citationGroupElement.appendChild(citationElement)

          labelIndex++
        }
      }
    }
  }
}

export function initCitations({ editor, citations, eventEmitters }: { editor: any; citations: CitationMinuteNodes; eventEmitters: EventEmitters }) {
  editor.on('SetContent', () => {
    insertCitationTags({ editor, citations, eventEmitters })
  })

  insertCitationTags({ editor, citations, eventEmitters })
}

export function getContentWithStrippedCitationsFromEditor({
  editor,
  citations,
  eventEmitters,
}: {
  editor: any
  citations: CitationMinuteNodes | null
  eventEmitters: EventEmitters
}): string {
  if (citations === null) {
    return editor.getContent()
  }

  editor.dom.remove(editor.dom.select('span.citation-group'))

  const content = editor.getContent()

  insertCitationTags({ editor, citations, eventEmitters })

  return content
}
